import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CARD, INPUT, BTN, BTN_GHOST, PageTitle, Table, useList } from "@/pages/admin/ui";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function CrudManager({ title, subtitle, path, columns, fields, defaults = {}, testid }) {
  const { data, loading, reload } = useList(path, [path]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ ...defaults }); setOpen(true); };
  const openEdit = (row) => {
    const f = { ...row };
    fields.forEach((fl) => { if (fl.type === "tags" && Array.isArray(f[fl.key])) f[fl.key] = f[fl.key].join(", "); });
    setForm(f); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    fields.forEach((fl) => {
      if (fl.type === "tags") payload[fl.key] = (payload[fl.key] || "").split(",").map((s) => s.trim()).filter(Boolean);
      if (fl.type === "number") payload[fl.key] = Number(payload[fl.key] || 0);
    });
    try {
      if (form.id) await api.put(`${path}/${form.id}`, payload);
      else await api.post(path, payload);
      toast.success("Saved");
      setOpen(false); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`${path}/${row.id}`); toast.success("Deleted"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid={`admin-${testid}`}>
      <PageTitle title={title} subtitle={subtitle} right={<button className={BTN} onClick={openNew} data-testid={`${testid}-add`}><Plus className="h-4 w-4" /> Add New</button>} />
      {loading ? <div className="text-sm text-[#64748B]">Loading…</div> : (
        <Table headers={[...columns.map((c) => c.label), "Actions"]}>
          {data.map((row) => (
            <tr key={row.id} className="hover:bg-[#F8F9FA]">
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 text-[#334155]">
                  {c.render ? c.render(row) : (Array.isArray(row[c.key]) ? row[c.key].join(", ") : String(row[c.key] ?? "—")).slice(0, 80)}
                </td>
              ))}
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <button className={BTN_GHOST + " !p-2"} onClick={() => openEdit(row)} data-testid={`${testid}-edit`}><Pencil className="h-4 w-4" /></button>
                  <button className={BTN_GHOST + " !p-2 text-red-600"} onClick={() => remove(row)} data-testid={`${testid}-delete`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </td>
            </tr>
          ))}
          {data.length === 0 && <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-[#94A3B8]">No records yet.</td></tr>}
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{form?.id ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4" data-testid={`${testid}-form`}>
              {fields.map((fl) => (
                <div key={fl.key}>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">{fl.label}</label>
                  {fl.type === "textarea" ? (
                    <textarea rows={3} className={INPUT} value={form[fl.key] || ""} onChange={(e) => setForm({ ...form, [fl.key]: e.target.value })} />
                  ) : fl.type === "select" ? (
                    <Select value={form[fl.key] || ""} onValueChange={(v) => setForm({ ...form, [fl.key]: v })}>
                      <SelectTrigger className={INPUT}><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{fl.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : fl.type === "toggle" ? (
                    <div className="flex items-center gap-2 pt-1"><Switch checked={!!form[fl.key]} onCheckedChange={(v) => setForm({ ...form, [fl.key]: v })} /><span className="text-sm text-[#64748B]">{form[fl.key] ? "Yes" : "No"}</span></div>
                  ) : (
                    <input type={fl.type === "number" ? "number" : "text"} className={INPUT} value={form[fl.key] ?? ""} onChange={(e) => setForm({ ...form, [fl.key]: e.target.value })} />
                  )}
                  {fl.hint && <p className="mt-1 text-xs text-[#94A3B8]">{fl.hint}</p>}
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className={BTN_GHOST} onClick={() => setOpen(false)}>Cancel</button>
                <button type="submit" className={BTN} disabled={saving} data-testid={`${testid}-save`}>{saving ? "Saving…" : "Save"}</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
